import type { Engine } from "../Engine";

const REPORT_MS = 2000;

type RenderTraceWindow = Window & {
  __DEBUG_RENDER_TRACE__?: boolean;
  enableRenderTrace?: () => void;
  disableRenderTrace?: () => void;
};

function win(): RenderTraceWindow | undefined {
  return typeof window !== "undefined" ? (window as RenderTraceWindow) : undefined;
}

/** Dev build'de varsayılan açık; `disableRenderTrace()` ile kapatılır. */
function isEnabled(): boolean {
  const w = win();
  if (!w) return false;
  if (w.__DEBUG_RENDER_TRACE__ === false) return false;
  if (localStorage.getItem("debugRenderTrace") === "0") return false;
  if (w.__DEBUG_RENDER_TRACE__ === true) return true;
  if (localStorage.getItem("debugRenderTrace") === "1") return true;
  return process.env.NODE_ENV !== "production";
}

function shouldSkipStackLine(line: string): boolean {
  const lower = line.toLowerCase();
  return (
    lower.includes("rendertrace") ||
    lower.includes("noterequestupdatedispatch") ||
    lower.includes("dispatchcustomevent") ||
    lower.includes("eventtarget.dispatchevent")
  );
}

function callerFromStack(stack: string): string {
  const lines = stack.split("\n");
  for (const line of lines.slice(2)) {
    const trimmed = line.trim();
    if (!trimmed || shouldSkipStackLine(trimmed)) continue;
    return trimmed;
  }
  return "unknown caller";
}

class RenderTraceState {
  requestUpdateCounts = new Map<string, number>();
  css2dRenders = 0;
  css3dRenders = 0;
  webglFrames = 0;
  lastReport = performance.now();
  hookInstalled = false;
}

const state = new RenderTraceState();

function traceLog(...args: unknown[]): void {
  //console.warn("[render-trace]", ...args);
}

function maybeReport(): void {
  if (!isEnabled()) return;
  const now = performance.now();
  if (now - state.lastReport < REPORT_MS) return;

  const requestRows = [...state.requestUpdateCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([caller, count]) => ({ caller, count }));

  traceLog(
    `${REPORT_MS}ms özet → webgl=${state.webglFrames} css2d=${state.css2dRenders} css3d=${state.css3dRenders} request-update=${requestRows.reduce((s, r) => s + r.count, 0)}`,
  );
  if (requestRows.length > 0) {
    //console.table(requestRows);
  }

  state.requestUpdateCounts.clear();
  state.css2dRenders = 0;
  state.css3dRenders = 0;
  state.webglFrames = 0;
  state.lastReport = now;
}

export function noteRequestUpdateDispatch(caller?: string): void {
  if (!isEnabled()) return;
  const site =
    caller ?? callerFromStack(new Error().stack ?? "");
  state.requestUpdateCounts.set(
    site,
    (state.requestUpdateCounts.get(site) ?? 0) + 1,
  );
  maybeReport();
}

export function noteCss2dRender(): void {
  if (!isEnabled()) return;
  state.css2dRenders++;
  maybeReport();
}

export function noteCss3dRender(): void {
  if (!isEnabled()) return;
  state.css3dRenders++;
  maybeReport();
}

export function noteWebglFrame(): void {
  if (!isEnabled()) return;
  state.webglFrames++;
  maybeReport();
}

function bindWindowControls(): void {
  const w = win();
  if (!w) return;

  w.enableRenderTrace = () => {
    localStorage.setItem("debugRenderTrace", "1");
    w.__DEBUG_RENDER_TRACE__ = true;
    state.lastReport = performance.now();
    traceLog("açıldı — 2 saniyede bir özet (webgl / css2d / request-update)");
  };

  w.disableRenderTrace = () => {
    localStorage.setItem("debugRenderTrace", "0");
    w.__DEBUG_RENDER_TRACE__ = false;
    traceLog("kapatıldı");
  };
}

bindWindowControls();

export function installRenderTrace(engine: Engine): void {
  if (state.hookInstalled) return;
  state.hookInstalled = true;

  const originalDispatch = engine.dispatchCustomEvent.bind(engine);
  engine.dispatchCustomEvent = (
    event: string,
    object?: { detail?: unknown; event?: Event },
  ) => {
    if (event === "request-update") {
      noteRequestUpdateDispatch(callerFromStack(new Error().stack ?? ""));
    }
    return originalDispatch(event, object);
  };

  if (isEnabled()) {
    traceLog(
      "aktif (dev varsayılan). Kapat: disableRenderTrace()",
    );
  } else {
    traceLog(
      "kapalı. Aç: enableRenderTrace()",
    );
  }
}
